import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import routesConfig from 'constants/routesConfig.json';

// components
import ApiLoading from 'shared/ApiLoading';
import ErrorBoundary from 'layout/components/ErrorBoundary';
import SiteOverview from 'teamLeadInsights/components/SiteOverview';
import SiteReport from 'teamLeadInsights/components/SiteReport';
import TeamOverview from 'teamLeadInsights/components/TeamOverview';
import PageShell from 'layout/components/PageShell';

// hooks
import useTeamLeadInsights from 'teamLeadInsights/hooks/teamLeadApi';

// styles
import * as PageLayout from 'layout/styles/PageLayout.style';
import * as Styled from 'teamLeadInsights/styles/TeamOverview.style';
import * as ViewTeamLeadStyles from 'teamLeadInsights/styles/ViewTeamLead.style';
import * as Text from 'theme/components/Text.style';

export default function TeamLeadInsights(): JSX.Element {
  const { data: insights, isLoading, isError } = useTeamLeadInsights();
  const { t } = useTranslation('pageTeamLeadInsights');
  const { t: tSidePanel } = useTranslation('sidePanel');
  const history = useHistory();
  const posthog = usePostHog();
  const canAccessShiftNotes = posthog.isFeatureEnabled('shift_notes');

  const handleBackToInsights = useCallback(() => {
    history.push(routesConfig.INSIGHTS_PAGE);
  }, [history]);

  if (isLoading)
    return (
      <PageLayout.Page>
        <ApiLoading />
      </PageLayout.Page>
    );
  if (isError)
    return (
      <Styled.TopCard>
        <Text.P3>
          {t(
            'insights.Your insights cannot be loaded right now. Please, try again later.'
          )}
        </Text.P3>
      </Styled.TopCard>
    );

  return (
    <ErrorBoundary>
      <PageLayout.Page style={canAccessShiftNotes ? { gap: 0 } : undefined}>
        <PageShell
          title={tSidePanel('Insights')}
          onBackClick={canAccessShiftNotes ? handleBackToInsights : undefined}
          showOnDesktop={canAccessShiftNotes}
        >
          <ViewTeamLeadStyles.Sections>
            {canAccessShiftNotes && <SiteReport />}
            <ViewTeamLeadStyles.Section>
              <TeamOverview
                onboardingRate={insights.onboarding_rate}
                sessionCount={insights.session_count}
                recentSessionsCount={insights.recent_sessions_count}
              />
              <Styled.TeamCardSection>
                {insights.insights.map((insight) => (
                  <SiteOverview key={insight.site} insight={insight} />
                ))}
              </Styled.TeamCardSection>
            </ViewTeamLeadStyles.Section>
          </ViewTeamLeadStyles.Sections>
        </PageShell>
      </PageLayout.Page>
    </ErrorBoundary>
  );
}
